import {announce} from '@github-ui/aria-live'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useMediaQuery} from '@github-ui/use-media-query'
import {BookIcon, BugIcon, CopilotIcon, RepoIcon, TasklistIcon, XIcon} from '@primer/octicons-react'
import {Banner, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {type RefObject, useEffect, useId, useRef, useState} from 'react'

import {
  usePersonaOnboarding,
  usePersonaTaskIntro,
  useSuggestedTasksDismissal,
} from '../../contexts/PersonaOnboardingContext'
import {type PersonaTask, personaTasks} from '../../data/persona-tasks'
import type {CopilotPersona} from '../../data/personas'
import {reportPersonaTaskSelected} from '../../utils/persona-onboarding-telemetry'
import styles from './PersonaTaskSuggestions.module.css'

export function PersonaTaskSuggestions({
  persona,
  onSelect,
  returnFocusRef,
}: {
  persona: CopilotPersona
  onSelect: (task: PersonaTask) => void
  returnFocusRef: RefObject<HTMLTextAreaElement | null>
}) {
  const onboarding = usePersonaOnboarding()
  const sectionRef = useRef<HTMLElement>(null)
  const id = useId()
  const {dismiss, errorMessage} = useSuggestedTasksDismissal()
  const animate = usePersonaTaskIntro()
  const [phase, setPhase] = useState<'loading' | 'revealing' | 'ready'>(animate ? 'loading' : 'ready')
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', () => setPhase('ready'))
  const loading = !reducedMotion && phase === 'loading'

  useEffect(() => {
    if (phase !== 'loading' || reducedMotion) return
    const timeout = window.setTimeout(() => setPhase('revealing'), 600)
    return () => window.clearTimeout(timeout)
  }, [phase, reducedMotion])

  useEffect(() => {
    if (animate) announce(loading ? 'Preparing suggested tasks...' : 'Suggested tasks are ready.')
  }, [animate, loading])

  if (onboarding?.suggestedTasksDismissed) return null

  const tasks = personaTasks[persona]
  const icons =
    persona === 'software_development_it' ? [BugIcon, RepoIcon, CopilotIcon] : [TasklistIcon, BookIcon, CopilotIcon]

  return (
    <section ref={sectionRef} className={styles.Suggestions} aria-labelledby={`${id}-heading`}>
      <div className={styles.Header}>
        <h2 id={`${id}-heading`} className={styles.Heading}>
          Suggested for you
        </h2>
        <IconButton
          icon={XIcon}
          aria-label="Dismiss suggested tasks"
          variant="invisible"
          size="small"
          aria-describedby={errorMessage ? `${id}-error` : undefined}
          onClick={() => {
            const shouldRestoreFocus = sectionRef.current?.contains(document.activeElement)
            if (dismiss() && shouldRestoreFocus) returnFocusRef.current?.focus()
          }}
        />
      </div>
      {errorMessage && (
        <div id={`${id}-error`}>
          <Banner variant="critical">
            <Banner.Title as="h3">{errorMessage}</Banner.Title>
          </Banner>
        </div>
      )}
      <div className={styles.Cards} onFocusCapture={() => setPhase('ready')}>
        {loading
          ? tasks.map(task => (
              <div key={task.id} className={styles.SkeletonCard} aria-hidden="true">
                <span className={styles.SkeletonIcon} />
                <span className={styles.SkeletonTitle} />
                <span className={styles.SkeletonDescription} />
              </div>
            ))
          : tasks.map((task, index) => {
              const Icon = icons[index] ?? CopilotIcon
              return (
                <button
                  key={task.id}
                  type="button"
                  className={clsx(styles.Card, {[styles.Reveal]: phase === 'revealing' && !reducedMotion})}
                  onAnimationEnd={() => {
                    if (index === tasks.length - 1) setPhase('ready')
                  }}
                  aria-labelledby={`${id}-${task.id}-title`}
                  aria-describedby={`${id}-${task.id}-description`}
                  onClick={() => {
                    onSelect(task)
                    reportPersonaTaskSelected(persona, task)
                    sendEvent('dotcom_chat.activate', {
                      target: 'PERSONA_TASK_SELECTED',
                      mode: 'immersive',
                      task: task.id,
                    })
                  }}
                >
                  <Icon className={styles.Icon} aria-hidden="true" />
                  <span id={`${id}-${task.id}-title`} className={styles.Title}>
                    {task.title}
                  </span>
                  <span id={`${id}-${task.id}-description`} className={styles.Description}>
                    {task.description}
                  </span>
                </button>
              )
            })}
      </div>
    </section>
  )
}
